import { useState } from 'react';
import { useCashflow } from '../context/CashflowContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const COLOR_OPTIONS = [
  '#15803d', // Green
  '#7c3aed', // Purple
  '#0891b2', // Cyan
  '#b45309', // Amber
  '#be123c', // Rose
  '#4f46e5', // Indigo
  '#78716C', // Stone
  '#dc2626', // Red
  '#2563eb', // Blue
  '#059669', // Emerald
];

export default function CategoriesPage() {
  const { categories, activeCashflow, createCategory, deleteCategory, loading } = useCashflow();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    color: COLOR_OPTIONS[0],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await createCategory(formData);
      toast.success('Category created');
      setIsDialogOpen(false);
      setFormData({ name: '', color: COLOR_OPTIONS[0] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating category');
    }
  };

  const handleDelete = async (id, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete default categories');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(id);
        toast.success('Category deleted');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error deleting category');
      }
    }
  };

  if (loading || !activeCashflow) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const defaultCategories = categories.filter(c => c.is_default);
  const customCategories = categories.filter(c => !c.is_default);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Categories
          </h1>
          <p className="text-stone-500 mt-1">
            Organize your expenses with categories
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Add New Category
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Marketing"
                  className="rounded-[4px]"
                  data-testid="category-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`color-picker-swatch ${formData.color === color ? 'ring-2 ring-stone-900 ring-offset-2' : ''}`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-[4px]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
                  data-testid="category-submit-btn"
                >
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Default Categories */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Default Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {defaultCategories.map((category) => (
              <div 
                key={category.id}
                className="flex items-center gap-3 p-4 bg-stone-50 rounded-[4px]"
              >
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <span className="font-medium text-stone-900">{category.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Categories */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Custom Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          {customCategories.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No custom categories yet</p>
              <p className="text-sm text-stone-400">Add a category to organize your expenses</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customCategories.map((category) => (
                <div 
                  key={category.id}
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-[4px] group"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="font-medium text-stone-900">{category.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(category.id, category.is_default)}
                    className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600"
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
